import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Button, PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import { useDebouncedState } from "src/utils/state";
import { ApiInternal } from "src/api/api_internal";
import { SimpleGroup, SimpleUser } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link GroupAddMemberDialog}
 */
export type GroupAddMemberDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the popup should be closed */
    onClose: () => void;
    /** Group to add the member to */
    group: SimpleGroup | undefined;
    /** Callback when a member was added */
    onMemberAdd: () => void;
};

/**
 * Dialog to add a member to a group
 */
export default function GroupAddMemberDialog(props: GroupAddMemberDialogProps) {
    const [t] = useTranslation("dialog-group-add-member");
    const [tg] = useTranslation();

    const [_, debouncedQuery, setDebouncedQuery] = useDebouncedState("", 500);
    const [users, setUsers] = React.useState<Array<SimpleUser>>([]);

    const form = useForm({
        defaultValues: {
            member: null as null | SimpleUser,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!props.group) return;
                if (!value.member) {
                    return {
                        fields: {
                            member: t("error.field-must-be-filled"),
                        },
                    };
                }

                const res = await ApiInternal.groups.local.addMember(props.group.uuid, value.member.uuid);
                if (res.result === "Err") {
                    return {
                        fields: {
                            member: res.error.already_part_of_group ? t("error.already-part-of-group") : undefined,
                        },
                    };
                }

                props.onMemberAdd();
            },
        },
    });

    React.useEffect(() => {
        if (!props.group) return;
        ApiInternal.groups.local
            .getUsersNotInGroup(props.group.uuid, { filter_name: debouncedQuery })
            .then(({ list }) => setUsers(list));
    }, [debouncedQuery, props.group]);

    React.useEffect(() => {
        form.reset();
    }, [props.group, props.isOpen]);

    if (!props.group) return null;

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.add-member-to-group", { group: props.group.name })}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"member"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.new-member")}</RequiredLabel>
                                        <Combobox
                                            value={fieldApi.state.value}
                                            onChange={(v) => fieldApi.handleChange(v)}
                                            onClose={() => setDebouncedQuery("")}
                                            queryDisplay={(v) => (v !== null ? v.display_name : "")}
                                            onQueryChange={(q) => setDebouncedQuery(q)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                        >
                                            {users.map((user) => (
                                                <ComboboxOption value={user} key={user.uuid}>
                                                    <Label>{user.display_name}</Label>
                                                </ComboboxOption>
                                            ))}
                                        </Combobox>
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{tg("button.save")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
